import clsx from 'clsx';
import { type ChangeEvent, forwardRef } from 'react';

import { MagnifyingGlassIcon } from '@dynatrace/strato-icons';

import * as styles from './SearchInput.sty.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import type { FormControlStateProps } from '../../core/types/form-control-state.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import type { BaseInputProps } from '../_base-input/base-input.types.js';
import { BaseInput } from '../_base-input/BaseInput.js';
import { BaseInputButton } from '../_base-input/BaseInputButton.js';
import { BaseInputSuffix } from '../_base-input/BaseInputContent.js';
import type { FormControlRef } from '../shared-types.js';

const COMPONENT_NAME = 'SearchInput';

//#region Typings
/**
 * The properties of the SearchInput component.
 * @public
 */
export type SearchInputProps = Pick<
  BaseInputProps,
  | 'variant'
  | 'onBlur'
  | 'autoComplete'
  | 'placeholder'
  | 'minLength'
  | 'maxLength'
> &
  FormControlStateProps &
  WithChildren &
  StylingProps &
  DataTestId &
  MaskingProps &
  FormControlProps<string, (value: string) => void> &
  BehaviorTrackingProps;

//#endregion Typings

const SearchInputRoot = /* @__PURE__ */ forwardRef<
  FormControlRef<HTMLDivElement>,
  SearchInputProps
>((props, forwardedRef) => {
  const {
    children,
    onChange,
    value,
    defaultValue,
    placeholder,
    required,
    readOnly = false,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    className: consumerClassName,
    ...remainingProps
  }: SearchInputProps = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const [internalValue, setInternalValue] = useControllableState({
    defaultValue,
    value,
    onChange,
  });

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
  };

  function onClear() {
    setInternalValue('');
  }

  return (
    <BaseInput
      ref={forwardedRef}
      {...mergeProps(restProps, behavioralTrackingProps)}
      className={clsx(styles.searchInput, consumerClassName)}
      type="search"
      value={internalValue}
      required={required}
      readOnly={readOnly}
      onChange={handleOnChange}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-disabled={ariaDisabledProp}
      onClear={onClear}
      placeholder={placeholder}
      data-dt-component={COMPONENT_NAME}
      // The width of the input is handled via CSS and the parent elements,
      // so we set it to 0 here to prevent the default width behavior of inputs
      size={1}
    >
      <BaseInput.Prefix>
        <MagnifyingGlassIcon />
      </BaseInput.Prefix>
      {children}
    </BaseInput>
  );
});

/**
 * Use the `SearchInput` component for search queries.
 * It includes a built-in `Clear` button for quick resets.
 * @public
 */
export const SearchInput = Object.assign(SearchInputRoot, {
  Suffix: BaseInputSuffix,
  Button: BaseInputButton,
});

(SearchInput as typeof SearchInput & { displayName: string }).displayName =
  COMPONENT_NAME;
